import { jsx as _jsx } from "react/jsx-runtime";
import { ConfirmationModal, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useOverlayContext, useStoreActions } from '~/contexts';
import { useMutationState } from '~/hooks/reactQuery/useMutationState';
import { useLocaleKeys } from '~/i18n';
import { ErrorResponse, errorTextsPath } from '~/infra/ErrorResponse';
import { clientStateKey } from '~/pages/ClientsPage/ClientsPage.utils';
export const GetClientArchiveRestoreDeleteTID = ()=>getTestIds();
export function useClientArchiveRestoreDelete(param) {
    let { onComplete, onDeleteComplete = onComplete } = param;
    const { api } = useStoreActions();
    const { showModal, showErrorToast } = useOverlayContext();
    const { t: clientsDashboardTexts } = useLocaleKeys('pages.clientsDashboard');
    const { t: archiveConfirmationTexts } = useLocaleKeys('pages.clientsDashboard.archiveConfirmation');
    const { t: deleteConfirmationTexts } = useLocaleKeys('pages.clientsDashboard.deleteConfirmation');
    const { t: errorTexts } = useLocaleKeys(errorTextsPath);
    const { ClientArchiveRestoreDeleteTestIds: TID } = GetClientArchiveRestoreDeleteTID();
    const archiveMutation = useMutationState({
        mutationFn: (clientId)=>api.client.archive(clientId),
        withPageLoader: true,
        mutationName: 'archiveClient',
        toasts: {
            successMessage: clientsDashboardTexts.archiveSuccessToast(),
            successTestId: TID.ArchiveSuccessToast()
        },
        onSuccess: ()=>onComplete(),
        onError: (err)=>showErrorToast(new ErrorResponse(err).archiveClientErrorText(errorTexts), TID.ArchiveErrorToast())
    });
    const restoreMutation = useMutationState({
        mutationFn: (clientId)=>api.client.restore(clientId),
        withPageLoader: true,
        mutationName: 'restoreClient',
        toasts: {
            successMessage: clientsDashboardTexts.restoreSuccessToast(),
            successTestId: TID.RestoreSuccessToast()
        },
        onSuccess: ()=>onComplete(),
        onError: (err)=>showErrorToast(new ErrorResponse(err).restoreClientErrorText(errorTexts), TID.RestoreErrorToast())
    });
    const deleteMutation = useMutationState({
        mutationFn: (clientId)=>api.client.delete(clientId),
        withPageLoader: true,
        mutationName: 'deleteClient',
        toasts: {
            successMessage: clientsDashboardTexts.deleteSuccessToast(),
            successTestId: TID.DeleteSuccessToast()
        },
        onSuccess: ()=>onDeleteComplete(),
        onError: (err)=>showErrorToast(new ErrorResponse(err).deleteClientErrorText(errorTexts), TID.DeleteErrorToast())
    });
    const onRestore = async (client)=>{
        restoreMutation.mutate(client.id);
    };
    const onArchive = async (client)=>{
        if (client.isSample || client.archiveEligibility.type === 'notEligible') return;
        const stateKey = clientStateKey(client.archiveEligibility);
        const confirmed = await showModal({
            biName: 'clients_dashboard_archive_confirmation_confirm',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: 'clients_archive_confirm'
                        },
                        secondaryButton: {
                            name: 'clients_archive_cancel'
                        }
                    },
                    skin: "danger",
                    textAlignCenter: false,
                    title: archiveConfirmationTexts.title[stateKey](),
                    content: /*#__PURE__*/ _jsx(Typography, {
                        showWhiteSpaces: true,
                        children: archiveConfirmationTexts.content[stateKey]({
                            clientName: client.name
                        })
                    }),
                    primaryButtonLabel: archiveConfirmationTexts.primaryButton(),
                    secondaryButtonLabel: archiveConfirmationTexts.secondaryButton(),
                    onPrimaryButtonClick: ()=>dismissModal(true),
                    onSecondaryButtonClick: ()=>dismissModal(false),
                    onCloseButtonClick: ()=>dismissModal(false)
                });
            }
        });
        if (!confirmed) return;
        archiveMutation.mutate(client.id);
    };
    const onDelete = async (client)=>{
        if (client.isSample || client.deleteEligibility.type === 'notEligible') return;
        const stateKey = clientStateKey(client.deleteEligibility);
        const confirmed = await showModal({
            biName: 'clients_dashboard_delete_confirmation_confirm',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: 'clients_delete_confirm'
                        },
                        secondaryButton: {
                            name: 'clients_delete_cancel'
                        }
                    },
                    skin: "danger",
                    textAlignCenter: false,
                    title: deleteConfirmationTexts.title[stateKey](),
                    content: /*#__PURE__*/ _jsx(Typography, {
                        showWhiteSpaces: true,
                        children: deleteConfirmationTexts.content[stateKey]({
                            clientName: client.name
                        })
                    }),
                    primaryButtonLabel: deleteConfirmationTexts.primaryButton(),
                    secondaryButtonLabel: deleteConfirmationTexts.secondaryButton(),
                    onPrimaryButtonClick: ()=>dismissModal(true),
                    onSecondaryButtonClick: ()=>dismissModal(false),
                    onCloseButtonClick: ()=>dismissModal(false)
                });
            }
        });
        if (!confirmed) return;
        deleteMutation.mutate(client.id);
    };
    return {
        onArchive,
        onRestore,
        onDelete
    };
}
